import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { StudyShell } from '@/pages/learn/study/StudyShell';
import {
  getStudyHome,
  listStudyDocuments,
  STUDY_HOME_QUERY_KEY,
} from '@/pages/learn/study/studyApi';
import { listStudyNotes } from '@/pages/learn/study/studyNotesApi';
import { SAMPLE_PROFILE, sampleAnalysisPath } from './sampleProfile';

/** Demo-only presentation. It never opens account management, wallets, credentials or a real child. */
export function SampleStudentProfile() {
  const home = useQuery({ queryKey: STUDY_HOME_QUERY_KEY, queryFn: getStudyHome, staleTime: 0 });
  const documents = useQuery({
    queryKey: ['sample-profile-materials'],
    queryFn: listStudyDocuments,
    staleTime: 0,
  });
  const notes = useQuery({
    queryKey: ['sample-profile-notes'],
    queryFn: () => listStudyNotes({ status: 'active' }),
    staleTime: 0,
  });
  return (
    <StudyShell>
      <p className="text-xs font-extrabold uppercase tracking-wide text-brand-sky">
        Fictional student profile · Demo only
      </p>
      <h1 className="mt-2 text-3xl font-extrabold text-ink">{SAMPLE_PROFILE.name}</h1>
      <p className="mt-3 max-w-3xl text-lg leading-relaxed text-ink-soft">
        {SAMPLE_PROFILE.background}
      </p>
      <dl className="my-6 grid max-w-4xl gap-4 rounded-3xl border border-hairline bg-canvas-pure p-5 sm:grid-cols-2">
        {[
          ['Age', String(SAMPLE_PROFILE.age)],
          ['School year', `Year ${home.data?.academic_year?.year_level ?? SAMPLE_PROFILE.year}`],
          ['School', home.data?.school.name ?? SAMPLE_PROFILE.school],
          ['Location', SAMPLE_PROFILE.location],
          ['Term', home.data?.current_term?.label ?? SAMPLE_PROFILE.term],
          ['Current focus', 'Fractions revision before algebra'],
        ].map(([label, value]) => (
          <div key={label}>
            <dt className="text-sm font-bold text-slate2">{label}</dt>
            <dd className="mt-1 font-extrabold text-ink">{value}</dd>
          </div>
        ))}
      </dl>
      <section className="max-w-4xl rounded-3xl bg-wash-sky p-5">
        <h2 className="text-xl font-extrabold">Start with Alex’s current schoolwork</h2>
        <p className="my-3 leading-relaxed">
          The three-page source, readable notes and learning points are ready. Try a linked
          question, explain your reasoning, then check Review for ideas to revisit.
        </p>
        <Link className="btn-pill-primary inline-flex" to={sampleAnalysisPath}>
          Continue the fractions worksheet
        </Link>
      </section>
      <section className="my-6 max-w-4xl">
        <h2 className="text-xl font-extrabold">Already in this sample workspace</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {[
            [
              '/learn/study/materials',
              `${documents.data?.length ?? 1} saved sample material`,
              'Open its original pages or continue learning.',
            ],
            [
              '/learn/study/notes',
              `${notes.data?.items.length ?? 2} class and learning notes`,
              'Edit, save and reopen; previous notebook versions stay available.',
            ],
            [
              '/learn/study/working',
              'Saved writing and working',
              'Continue Alex’s partial reasoning or add your own steps.',
            ],
            [
              '/learn/study/review',
              `${home.data?.review_due_count ?? 0} review item(s) remaining`,
              'Earlier fictional mistakes and your demo actions stay local.',
            ],
            [
              '/learn/study/journey',
              'Learning history and reflection',
              'Trace the sample source, edits, attempts and revisits.',
            ],
            [
              '/learn/study/cheatsheets',
              'Fractions reference',
              'A prepared example reference, not an official published syllabus sheet.',
            ],
          ].map(([to, title, description]) => (
            <Link
              key={to}
              to={to}
              className="rounded-2xl border border-hairline bg-canvas-pure p-4 hover:bg-surface"
            >
              <h3 className="font-extrabold">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate2">{description}</p>
            </Link>
          ))}
        </div>
      </section>
      <p className="max-w-4xl border-t border-hairline pt-4 text-sm leading-relaxed text-slate2">
        Alex, the school and the learning history are fictional. Prepared examples are not a real AI
        run. Your edits and answers are held only in this demo document; Reset or reload starts
        over. Other subject spaces let you explore supported tools, without a claim that Alex
        completed every tool or curriculum. Classes, other Airbotix products, real accounts,
        payments and live AI are outside this sample.
      </p>
    </StudyShell>
  );
}
